import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import db from '../../db/db';
import Ticket from './Ticket';

const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DetalleVenta = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [venta, setVenta]         = useState(null);
    const [productos, setProductos] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [metodos, setMetodos]     = useState([]);
    const [eventos, setEventos]     = useState([]);
    const [sucursalNombre, setSucursalNombre] = useState('');
    const [mostrarTicket, setMostrarTicket]   = useState(false);

    const cargar = useCallback(async () => {
        const ventaId = parseInt(id, 10);
        const [v, prods, emps, mets, evts, sesion] = await Promise.all([
            db.ventas.get(ventaId),
            db.ventas_productos.where('venta_local_id').equals(ventaId).toArray(),
            db.empleados.toArray(),
            db.metodos.toArray(),
            db.eventos.toArray(),
            db.sesion.toArray(),
        ]);

        if (!v) { navigate('/ventas'); return; }

        setVenta(v);
        setEmpleados(emps);
        setMetodos(mets);
        setEventos(evts);

        const sucursalId = sesion[0]?.sucursal?._id || sesion[0]?.sucursal;
        const sucReg = await db.sucursales
            .filter(s => String(s._id || s.id) === String(sucursalId))
            .first();
        setSucursalNombre(sucReg?.nombre || '');

        const catalogo = await db.productos.toArray();
        setProductos(prods.map(p => {
            const prod = catalogo.find(c => c._id === p.producto || String(c.id) === String(p.producto));
            return { ...p, nombre: prod?.nombre || '—', tipo: prod?.tipo ?? null };
        }));
    }, [id, navigate]);

    useEffect(() => { cargar(); }, [cargar]);

    const resolverNombre = (empId) => {
        const emp = empleados.find(e => e._id === empId || String(e.id) === String(empId));
        if (!emp) return '—';
        return `${emp.nombre} ${emp.apellido_paterno || ''}`.trim();
    };
    const resolverMetodo = (metId) => {
        const m = metodos.find(m => m._id === metId || String(m.id) === String(metId));
        return m?.nombre || '—';
    };
    const resolverEvento = (evtId) => {
        const e = eventos.find(e => e._id === evtId || String(e.id) === String(evtId));
        return e?.nombre || null;
    };

    const handleImprimir = () => {
        const carritoTicket = productos.map(p => ({
            nombre:      p.nombre,
            cantidad:    p.cantidad,
            precio_total: p.precio,
            extras:      (p.extras || []).map(e => ({ nombre: e.nombre, precio: e.precio })),
        }));
        setMostrarTicket({
            folio:   venta.folio,
            fecha:   new Date(venta.fecha),
            vendedor: resolverNombre(venta.vendedor),
            sucursal: sucursalNombre,
            metodo:  venta.metodo ? resolverMetodo(venta.metodo) : null,
            cobrado: !venta.cortesia,
            carrito: carritoTicket,
            total:   venta.total,
        });
    };

    if (!venta) {
        return (
            <div style={styles.loadingWrap}>
                <span className="spinner-border" style={{ color: '#F5C800' }} role="status" />
            </div>
        );
    }

    const totalConos = productos
        .filter(p => p.tipo === 0)
        .reduce((acc, p) => acc + (p.cantidad || 0), 0);

    const fecha = new Date(venta.fecha);
    const fechaStr = fecha.toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const horaStr  = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    const evento   = resolverEvento(venta.evento);

    const tieneEmpleados = (venta.empleados || []).length > 0;

    return (
        <>
        {mostrarTicket && (
            <Ticket data={mostrarTicket} onCerrar={() => setMostrarTicket(false)} />
        )}

        <div style={styles.page}>

            {/* ── Encabezado ── */}
            <div style={styles.topBar}>
                <button style={styles.btnVolver} onClick={() => navigate('/ventas')}>
                    <i className="fas fa-arrow-left me-2"></i>
                    Ventas
                </button>
                <div style={styles.topRight}>
                    <h4 style={styles.titulo}>
                        Detalle de venta
                        <span style={styles.folioTag}>{venta.folio}</span>
                    </h4>
                    <span style={venta.status ? styles.badgeRealizada : styles.badgeCancelada}>
                        {venta.status ? 'Realizada' : 'Cancelada'}
                    </span>
                </div>
                <button style={styles.btnImprimir} onClick={handleImprimir}>
                    <i className="fas fa-print me-2"></i>
                    Imprimir
                </button>
            </div>

            {/* ── Info general ── */}
            <div style={styles.infoGrid}>
                <InfoItem icon="fa-calendar" label="Fecha" value={`${fechaStr.charAt(0).toUpperCase() + fechaStr.slice(1)} · ${horaStr}`} />
                <InfoItem icon="fa-user"     label="Vendedor" value={resolverNombre(venta.vendedor)} />
                <InfoItem icon="fa-credit-card" label="Método de pago" value={resolverMetodo(venta.metodo)} />
                <InfoItem icon="fa-ice-cream" label="Conos vendidos" value={totalConos} color="#48BB78" />
                {evento && <InfoItem icon="fa-calendar" label="Evento" value={evento} color="#A78BFA" />}
                {venta.cortesia && <InfoItem icon="fa-gift" label="Tipo de precio" value="Cortesía (100%)" color="#48BB78" />}
                {venta.especial && <InfoItem icon="fa-tag" label="Tipo de precio" value="Precio especial (50%)" color="#A78BFA" />}
            </div>

            {/* ── Cuerpo principal (productos + empleados) ── */}
            <div style={tieneEmpleados ? styles.bodyGrid : undefined}>

                {/* ── Productos ── */}
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <i className="fas fa-box me-2" style={{ color: '#F5C800' }}></i>
                        Productos
                    </div>
                    {productos.map((p, idx) => (
                        <div key={p.id} style={{
                            ...styles.prodFila,
                            borderBottom: idx < productos.length - 1 ? '1px solid #222' : 'none',
                        }}>
                            <div style={styles.prodInfo}>
                                <span style={styles.prodNombre}>{p.nombre}</span>
                                {p.extras?.length > 0 && (
                                    <span style={styles.prodExtras}>
                                        + {p.extras.map(e => e.nombre).join(', ')}
                                    </span>
                                )}
                            </div>
                            <div style={styles.prodDerecha}>
                                <span style={styles.prodCantidad}>x{p.cantidad}</span>
                                <span style={styles.prodPrecio}>${fmt(p.precio)}</span>
                                <span style={styles.prodSubtotal}>${fmt(p.precio * p.cantidad)}</span>
                            </div>
                        </div>
                    ))}
                    <div style={styles.totalRow}>
                        <span style={{ color: '#888', fontSize: '0.9rem' }}>Total</span>
                        <span style={styles.totalValor}>${fmt(venta.total)}</span>
                    </div>
                </div>

                {/* ── Empleados involucrados ── */}
                {tieneEmpleados && (
                    <div style={styles.card}>
                        <div style={styles.cardHeader}>
                            <i className="fas fa-users me-2" style={{ color: '#F5C800' }}></i>
                            Empleados involucrados
                            <span style={styles.empCount}>{venta.empleados.length}</span>
                        </div>
                        {venta.empleados.map((ev, idx) => (
                            <div key={ev.empleado} style={{
                                ...styles.empFila,
                                borderBottom: idx < venta.empleados.length - 1 ? '1px solid #222' : 'none',
                            }}>
                                <div style={styles.empInfo}>
                                    <div style={styles.empAvatar}>
                                        <i className="fas fa-user" style={{ color: '#555', fontSize: '0.9rem' }}></i>
                                    </div>
                                    <span style={styles.empNombre}>{resolverNombre(ev.empleado)}</span>
                                </div>
                                {ev.total_comision > 0 && (
                                    <div style={styles.empComisionWrap}>
                                        <span style={styles.empComisionLabel}>Comisión</span>
                                        <span style={styles.empComision}>${fmt(ev.total_comision)}</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

const InfoItem = ({ icon, label, value, color = '#F5C800' }) => (
    <div style={styles.infoItem}>
        <div style={{ ...styles.infoIconWrap, background: `${color}14`, border: `1px solid ${color}33` }}>
            <i className={`fas ${icon}`} style={{ color, fontSize: '0.95rem' }}></i>
        </div>
        <div>
            <div style={styles.infoLabel}>{label}</div>
            <div style={styles.infoValue}>{value}</div>
        </div>
    </div>
);

const styles = {
    page: {
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
    },
    bodyGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1.25rem',
        alignItems: 'start',
    },
    loadingWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 120px)',
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    btnVolver: {
        background: 'transparent',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.5rem 1rem',
        color: '#888',
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        flexShrink: 0,
    },
    btnImprimir: {
        background: 'transparent',
        border: '1px solid rgba(245,200,0,0.5)',
        borderRadius: '10px',
        padding: '0.5rem 1rem',
        color: '#F5C800',
        fontWeight: 600,
        fontSize: '0.9rem',
        cursor: 'pointer',
        flexShrink: 0,
        marginLeft: 'auto',
    },
    topRight: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
        flex: 1,
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    folioTag: {
        background: 'rgba(245,200,0,0.12)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '8px',
        padding: '0.15rem 0.65rem',
        fontSize: '0.85rem',
        fontFamily: 'monospace',
        fontWeight: 700,
    },
    badgeRealizada: {
        background: 'rgba(245,200,0,0.1)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.7rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    badgeCancelada: {
        background: 'rgba(229,62,62,0.1)',
        color: '#E53E3E',
        border: '1px solid rgba(229,62,62,0.3)',
        borderRadius: '20px',
        padding: '0.2rem 0.7rem',
        fontSize: '0.78rem',
        fontWeight: 700,
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '0.75rem',
    },
    infoItem: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '12px',
        padding: '1rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.85rem',
    },
    infoIconWrap: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    infoLabel: {
        color: '#666',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '0.2rem',
    },
    infoValue: {
        color: '#C0C0C0',
        fontWeight: 600,
        fontSize: '0.92rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
    },
    cardHeader: {
        background: '#111',
        borderBottom: '1px solid #2E2E2E',
        padding: '0.85rem 1.25rem',
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '0.95rem',
        display: 'flex',
        alignItems: 'center',
    },
    prodFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.9rem 1.25rem',
        gap: '1rem',
    },
    prodInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.2rem',
        flex: 1,
    },
    prodNombre: {
        color: '#C0C0C0',
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    prodExtras: {
        color: '#555',
        fontSize: '0.8rem',
    },
    prodDerecha: {
        display: 'flex',
        alignItems: 'center',
        gap: '1.25rem',
        flexShrink: 0,
    },
    prodCantidad: {
        color: '#666',
        fontSize: '0.88rem',
        fontWeight: 600,
        minWidth: '28px',
        textAlign: 'center',
    },
    prodPrecio: {
        color: '#555',
        fontSize: '0.88rem',
        minWidth: '70px',
        textAlign: 'right',
    },
    prodSubtotal: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '0.95rem',
        minWidth: '80px',
        textAlign: 'right',
    },
    totalRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.85rem 1.25rem',
        background: 'rgba(245,200,0,0.04)',
        borderTop: '1px solid #2E2E2E',
    },
    totalValor: {
        color: '#F5C800',
        fontWeight: 800,
        fontSize: '1.2rem',
    },
    empFila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.85rem 1.25rem',
    },
    empInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
    },
    empAvatar: {
        width: '34px',
        height: '34px',
        borderRadius: '50%',
        background: '#222',
        border: '1px solid #333',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    empNombre: {
        color: '#C0C0C0',
        fontWeight: 600,
        fontSize: '0.92rem',
    },
    empCount: {
        marginLeft: '0.5rem',
        background: 'rgba(245,200,0,0.12)',
        color: '#F5C800',
        border: '1px solid rgba(245,200,0,0.3)',
        borderRadius: '20px',
        padding: '0.05rem 0.5rem',
        fontSize: '0.75rem',
        fontWeight: 700,
    },
    empComisionWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '0.1rem',
    },
    empComisionLabel: {
        color: '#555',
        fontSize: '0.72rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
    },
    empComision: {
        color: '#48BB78',
        fontWeight: 700,
        fontSize: '0.95rem',
    },
};

export default DetalleVenta;
